/**
 * Utility function for making API requests with consistent error handling
 * @param {string} endpoint - The API endpoint to call
 * @param {Object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Object>} - Parsed JSON response
 * @throws {Error} - If the request fails or returns an error
 */
export const apiRequest = async (endpoint, options = {}) => {
    const defaultHeaders = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    };

    // Merge default headers with any provided headers
    const headers = {
        ...defaultHeaders,
        ...(options.headers || {})
    };

    try {
        const response = await fetch(endpoint, {
            ...options,
            headers
        });

        // Handle non-2xx responses
        if (!response.ok) {
            let errorMessage = `HTTP error! status: ${response.status}`;
            try {
                const errorData = await response.json();
                errorMessage = errorData.message || errorMessage;
            } catch (e) {
                // If we can't parse the error response, use the status text
                errorMessage = response.statusText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        // Handle empty responses (like 204 No Content)
        if (response.status === 204) {
            return { success: true };
        }

        // Parse and return the JSON response
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('API request failed:', error);
        throw new Error(error.message || 'An unexpected error occurred');
    }
};

/**
 * Creates an API client with predefined base URL and default options
 * @param {string} baseUrl - The base URL for all API requests
 * @returns {Object} - An object with HTTP methods
 */
export const createApiClient = (baseUrl) => {
    const makeRequest = (method) => async (endpoint, data = null, options = {}) => {
        const url = `${baseUrl}${endpoint}`;
        const requestOptions = {
            method,
            ...options,
            headers: {
                ...(options.headers || {})
            }
        };

        if (data) {
            requestOptions.body = JSON.stringify(data);
        }

        return apiRequest(url, requestOptions);
    };

    return {
        get: makeRequest('GET'),
        post: makeRequest('POST'),
        put: makeRequest('PUT'),
        delete: makeRequest('DELETE'),
        patch: makeRequest('PATCH')
    };
};
